import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  LegacyCard,
  Page,
  Badge,
  TextField,
  Label,
  Button,
  LegacyStack,
  IndexTable,
  PageActions,
  FormLayout,
  Layout,
  Select,
} from "@shopify/polaris";
import { ImportIcon, ExportIcon, EmailFollowUpIcon, UndoIcon } from "@shopify/polaris-icons";
import EmailPdfDialog from "./support/EmailPdfDialog";
import DeleteConfirmationDialog from "../common/DeleteConfirmationDialog";
import CostAdjustments from "./support/CostAdjustments";
import ConfirmationDialog from "../common/ConfirmationDialog";
import CustomDatePicker from "../common/CustomDatePicker/CustomDatePicker";
import {
  deletePurchaseOrder,
  updatePurchaseOrder,
  getPurchaseOrderHorseVariants,
  importPurchaseOrderLineItems,
  emailPdf,
  downloadFile,
  useUser,
} from "../../api_utils/requests";
import { getTotal } from "./support/utils";
import {
  addGlowAnimation,
  cloneNestedObjects,
  commaDelimiter,
  datePlaceholder,
  extractMessageFromError,
  formatMoney,
  makeLineItemsMap,
  vendorPath,
} from "../../helper_functions/utils";
import { ImportFile } from "../common/ImportFile";
import { ErrorBanner } from "../common/ErrorBanner";
import { Footer } from "../common/Footer";
import type { PurchaseOrderLineItemUpdatePayload } from "../../api_utils/types";
import {
  PurchaseOrderState,
  type IPurchaseOrder,
  type IPurchaseOrderLineItem,
  type PurchaseOrderLineItemsQueryParams,
} from "../../api_utils/types";
import { PURCHASE_ORDER_HELP_PAGE_URL } from "../../constants";
import Scanner from "../common/Scanner";
import SaveBar from "../common/SaveBar";
import { useNavigate } from "react-router-dom";
import Comments from "./Comments";
import PurchaseOrderOrderedRow from "./ordered/PurchaseOrderOrderedRow";
import HorseLink from "../common/HorseLink";
import {
  defaultOrderedClosedPurchaseOrderSortValue,
  orderedClosedPurchaseOrderSortOptions,
} from "./support/orderedClosedPurchaseOrderSortOptions";

const getPurchaseOrderLineItemsPayload = (
  purchaseOrderLineItems: PurchaseOrderLineItems,
): PurchaseOrderLineItemUpdatePayload[] =>
  Object.values(purchaseOrderLineItems).map(
    (purchaseOrderLineItem: SortablePurchaseOrderLineItem): PurchaseOrderLineItemUpdatePayload => {
      return {
        id: purchaseOrderLineItem.id,
        quantity: purchaseOrderLineItem.quantity || 0,
        received: purchaseOrderLineItem.received || 0,
        cost: purchaseOrderLineItem.cost,
      };
    },
  );

type SortablePurchaseOrderLineItem = IPurchaseOrderLineItem & {
  sortIndex: number;
};

type PurchaseOrderLineItems = Record<number, SortablePurchaseOrderLineItem>;

function Index({
  purchaseOrder: initialPurchaseOrder,
  refetch,
}: {
  readonly purchaseOrder: IPurchaseOrder;
  readonly refetch: () => void;
}): React.JSX.Element {
  const today = useMemo(() => datePlaceholder(new Date()), []);

  const initialPurchaseOrderWithArrivalDate = {
    ...initialPurchaseOrder,
    arrival_date: initialPurchaseOrder.arrival_date ? new Date(initialPurchaseOrder.arrival_date) : new Date(),
  };
  const [purchaseOrder, setPurchaseOrder] = useState<IPurchaseOrder>(initialPurchaseOrderWithArrivalDate);
  const originalPurchaseOrder = useRef<IPurchaseOrder>(initialPurchaseOrderWithArrivalDate);
  const [importCSVModalIsOpen, setImportCSVModalIsOpen] = useState(false);
  const [polis, setPolis] = useState<PurchaseOrderLineItems>(makeLineItemsMap<SortablePurchaseOrderLineItem>([]));
  const [isDirty, setIsDirty] = useState(false);
  // const [importEntryNumber, setImportEntryNumber] = useState(purchaseOrder.entry_summary_id || '');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    shopify.loading(loading);
  }, [loading]);

  const [emailPdfDialogActive, setEmailPdfDialogActive] = useState(false);
  const [syncCostsModalActive, setSyncCostsModalActive] = useState(false);
  const [syncCostsRequested, setSyncCostsRequested] = useState(false);
  const [closePurchaseOrderModalActive, setClosePurchaseOrderModalActive] = useState(false);
  const [deletionDialogueActive, setDeletionDialogueActive] = useState(false);

  const {
    data: {
      user: { syncbackSetting },
    },
  } = useUser();

  const [errorMessage, setErrorMessage] = useState("");
  const [sortedValue, setSortedValue] = useState(defaultOrderedClosedPurchaseOrderSortValue);

  const query = useMemo<PurchaseOrderLineItemsQueryParams>(
    () => ({
      by_sort: sortedValue,
    }),
    [sortedValue],
  );

  const automaticSyncback = syncbackSetting === "only_cost" || syncbackSetting === "all_syncback";

  const handleReceiveAll = useCallback((): void => {
    const newQuantity = Object.values(polis).reduce<PurchaseOrderLineItems>(
      (acc: PurchaseOrderLineItems, value: SortablePurchaseOrderLineItem): PurchaseOrderLineItems => {
        // copies the purchase quantity
        const copiedValue = value.quantity || 0;
        const newValue = copiedValue < 0 ? 0 : copiedValue;
        acc[value.id] = {
          ...value,
          quantity: newValue,
          received: newValue,
        } satisfies IPurchaseOrderLineItem;
        return acc;
      },
      {},
    );
    setPolis(newQuantity);
    setIsDirty(true);
  }, [polis, setPolis, setIsDirty]);

  const originalPolis = useRef<PurchaseOrderLineItems>(polis);
  const fetchAndUpdateVariant = useCallback((): Promise<void> => {
    return getPurchaseOrderHorseVariants(purchaseOrder.id, query)
      .then((response) => {
        const purchaseOrderLineItems = makeLineItemsMap<SortablePurchaseOrderLineItem>(response as SortablePurchaseOrderLineItem[]);
        setPolis(purchaseOrderLineItems);
        originalPolis.current = purchaseOrderLineItems;
      })
      .catch(() => {
        setPolis(cloneNestedObjects(originalPolis.current));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [purchaseOrder.id, query]);

  useEffect(() => {
    void fetchAndUpdateVariant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const summarySaveCallback = useRef<() => Promise<void>>(() => Promise.resolve());
  const summaryDiscardCallback = useRef<() => void>(() => null);
  const setSummaryIsDirty = useCallback((summaryIsDirty: boolean): void => {
    setIsDirty(isDirty || summaryIsDirty);
  }, [isDirty]);
  
  const handleDiscardAction = useCallback((): void => {
    setIsDirty(false);
    summaryDiscardCallback.current();
    setPurchaseOrder(originalPurchaseOrder.current);
    void fetchAndUpdateVariant();
  }, [summaryDiscardCallback, originalPurchaseOrder, setPurchaseOrder, fetchAndUpdateVariant]);

  const payload = useMemo(() => {
    return {
      purchase_order_line_items_attributes: getPurchaseOrderLineItemsPayload(polis),
      note: purchaseOrder.note,
      arrival_date: purchaseOrder.arrival_date,
      label: purchaseOrder.label,
    };
  }, [polis, purchaseOrder.note, purchaseOrder.arrival_date, purchaseOrder.label]);

  const handleStateChange = useCallback(async (state: PurchaseOrderState, successMessage: string): Promise<void> => {
    setLoading(true);
    const mPayload = {
      purchase_order: {
        sync_costs_requested: syncCostsRequested || automaticSyncback,
        state,
      },
    };
    try {
      await updatePurchaseOrder(purchaseOrder.id, mPayload)
      shopify.toast.show(successMessage);
      refetch();
    } catch (err: unknown) {
        Rollbar.error(err, mPayload);
        const message = extractMessageFromError(err);
        setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  }, [purchaseOrder.id, refetch, setLoading, setErrorMessage, syncCostsRequested, automaticSyncback]);

  const handleMarkAsReceived = useCallback(async (): Promise<void> => {
    await handleStateChange(PurchaseOrderState.Closed, "Purchase order closed");
  }, [handleStateChange]);

  const handleRevertToDraft = useCallback(async (): Promise<void> => {
    await handleStateChange(PurchaseOrderState.Draft, "Purchase order reverted");
  }, [handleStateChange]);

  const handleSaveOrdered = useCallback(async (): Promise<void> => {
    setLoading(true);
    const mPayload = {
      purchase_order: {
        ...payload,
      },
    };
    const summarySavePromise = summarySaveCallback.current();
    const updatePurchaseOrderPromise = updatePurchaseOrder(purchaseOrder.id, mPayload);

    try {
      await Promise.all([summarySavePromise, updatePurchaseOrderPromise])
      shopify.toast.show("Save successful");
      originalPurchaseOrder.current = purchaseOrder;
      setIsDirty(false);
    } catch (err: unknown) {
        Rollbar.error(err, mPayload);
        const message = extractMessageFromError(err);
        setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  }, [summarySaveCallback, purchaseOrder, payload, setIsDirty, setLoading]);

  const handleLineItemChange = useCallback(
    (mLabel: string, id: number) =>
      (newValue?: string): void => {
        const cleanValue = newValue?.replace(/[^\d.-]/g, "");
        const newLineItems = { ...polis, [id]: { ...polis[id], [mLabel]: cleanValue } };
        setPolis(newLineItems);
        setIsDirty(true);
      },
    [polis, setPolis, setIsDirty],
  );

  const handleNoteChange = useCallback(
    (note: string): void => {
      setPurchaseOrder({ ...purchaseOrder, note });
      setIsDirty(true);
    },
    [purchaseOrder, setPurchaseOrder, setIsDirty],
  );

  const handleDateChange = useCallback((arrival_date: Date): void => {
    setPurchaseOrder({ ...purchaseOrder, arrival_date });
    setIsDirty(true);
  }, [purchaseOrder, setPurchaseOrder, setIsDirty]);

  const handleLabelChange = useCallback(
    (label: string): void => {
      setPurchaseOrder({ ...purchaseOrder, label });
      setIsDirty(true);
    },
    [purchaseOrder, setPurchaseOrder, setIsDirty],
  );

  const handleEmailPdfModalClose = useCallback((): void => {
    setEmailPdfDialogActive(false);
  }, []);

  // const handleImportEntryChange = (value) => {
  //   setImportEntryNumber(value);
  //   setIsDirty(true);
  // };

  const glowRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const incrementLineItem = useCallback((id: number): void => {
    setPolis((prevLineItems: PurchaseOrderLineItems) => {
      const existingLineItem: SortablePurchaseOrderLineItem = prevLineItems[id];
      const existingQuantity = existingLineItem.received || 0;
      const newQuantity = existingQuantity + 1;
      const updatedLineItem: SortablePurchaseOrderLineItem = {
        ...existingLineItem,
        received: newQuantity,
      };

      return {
        ...prevLineItems,
        [id]: updatedLineItem,
      };
    });
    setIsDirty(true);

    // Target the sibling of the input: .Polaris-TextField__Backdrop
    if (glowRef.current) {
      clearTimeout(glowRef.current);
    }
    glowRef.current = addGlowAnimation(`input[name="received"][id="${id}"] ~ .Polaris-TextField__Backdrop`);
  }, []);

  const openDeletionDialogue = useCallback(() => {
    setDeletionDialogueActive(true);
  }, [setDeletionDialogueActive]);

  const total = useMemo(() => getTotal(polis), [polis]);
  const sortedLineItems = useMemo(() => {
    return Object.values(polis).sort(
      (leftLineItem, rightLineItem) => leftLineItem.sortIndex - rightLineItem.sortIndex,
    );
  }, [polis]);

  const navigate = useNavigate();

  const exportPdf = useCallback(async (): Promise<void> => {
    const purchaseOrderName = purchaseOrder.label || purchaseOrder.id;
    const filename = `Horse Purchase Order ${purchaseOrderName}.pdf`;
    setLoading(true);
    try {
      await downloadFile(`/purchase_orders/${purchaseOrder.id}.pdf?${window.location.search}`, filename)
    } catch (err: unknown) {
        Rollbar.error(err, { purchaseOrderId: purchaseOrder.id });
        setErrorMessage("Failed to generate PDF");
    } finally {
      setLoading(false);
    }
  }, [purchaseOrder, setLoading, setErrorMessage]);

  const openEmailPdfDialog = useCallback((): void => {
    setEmailPdfDialogActive(true);
  }, [setEmailPdfDialogActive]);

  const exportCsv = useCallback(async (): Promise<void> => {
    const purchaseOrderName = purchaseOrder.label || purchaseOrder.id;
    const filename = `Horse Purchase Order ${purchaseOrderName}.csv`;
    await downloadFile(`/purchase_orders/${purchaseOrder.id}.csv${window.location.search}`, filename);
  }, [purchaseOrder]);

  const handleExportDymoCsv = async (): Promise<void> => {
    const purchaseOrderName = purchaseOrder.label || purchaseOrder.id;
    const filename = `${purchaseOrderName} Dymo Labels by Horse.csv`;
    await downloadFile(`/purchase_orders/${purchaseOrder.id}/dymo_csv`, filename);
  };

  const openImportCsvModal = useCallback((): void => {
    setImportCSVModalIsOpen(true);
  }, [setImportCSVModalIsOpen]);

  const backAction = useCallback(async (): Promise<void> => {
    await shopify.saveBar.leaveConfirmation();
    navigate("/purchase_orders");
  }, [navigate]);

  const closePurchaseOrder = useCallback(async (): Promise<void> => {
    if (automaticSyncback) {
      await handleMarkAsReceived();
    } else {
      setSyncCostsModalActive(true);
    }
  }, [automaticSyncback, handleMarkAsReceived, setSyncCostsModalActive]);

  return (
    purchaseOrder && (
      <Page
        actionGroups={[
          {
            title: "More actions",
            actions: [
              {
                content: "Export PDF",
                onAction: exportPdf,
                icon: ExportIcon,
              },
              {
                content: "Email PDF",
                onAction: openEmailPdfDialog,
                icon: EmailFollowUpIcon,
              },
              {
                content: "Export CSV",
                onAction: exportCsv,
                icon: ExportIcon,
              },
              {
                content: "Export Dymo CSV",
                onAction: handleExportDymoCsv,
                icon: ExportIcon,
              },
              {
                content: "Import CSV",
                onAction: openImportCsvModal,
                icon: ImportIcon,
              },
              {
                content: "Revert to draft",
                onAction: handleRevertToDraft,
                disabled: isDirty,
                prefix: <UndoIcon />,
              },
            ],
          },
        ]}
        backAction={{
          content: "Purchase orders",
          onAction: backAction,
        }}
        primaryAction={{
          content: "Close purchase order",
          disabled: isDirty,
          onAction: closePurchaseOrder,
        }}
        secondaryActions={[
          {
            content: "Receiving history",
            // url: `/inventory_level_histories?purchase_order_id=${purchaseOrder.id}`,
            plain: true,
            onAction: async (): Promise<void> => {
              await shopify.saveBar.leaveConfirmation();
    
              navigate(`/inventory_level_histories?purchase_order_id=${purchaseOrder.id}`);
            },
          },
        ]}
        title="Receive purchase order"
        titleMetadata={<Badge tone="info">Ordered</Badge>}
      >
        <SaveBar handleDiscard={handleDiscardAction} handleSave={handleSaveOrdered} isDirty={isDirty} />

        <EmailPdfDialog
          active={emailPdfDialogActive}
          onClose={handleEmailPdfModalClose}
          onSend={emailPdf}
          purchaseOrder={purchaseOrder}
          setErrorMessage={setErrorMessage}
        />
        <ConfirmationDialog
          active={syncCostsModalActive}
          cancelText="Continue"
          content="Do you want to sync these costs to Shopify? Doing so means overwriting the costs of these variants in your Shopify store(s)."
          okText="Sync costs and continue"
          onCancel={() => {
            setSyncCostsModalActive(false);
            setSyncCostsRequested(false);
            setClosePurchaseOrderModalActive(true);
          }}
          onClose={() => {
            setSyncCostsModalActive(false);
          }}
          onOk={() => {
            setSyncCostsModalActive(false);
            setSyncCostsRequested(true);
            setClosePurchaseOrderModalActive(true);
          }}
          title="Sync costs to Shopify"
        />
        <ConfirmationDialog
          active={closePurchaseOrderModalActive}
          cancelText="No"
          content="Are you sure you want to close this purchase order?"
          okText="Yes"
          onCancel={() => {
            setClosePurchaseOrderModalActive(false);
          }}
          onClose={() => {
            setClosePurchaseOrderModalActive(false);
          }}
          onOk={async () => {
            setClosePurchaseOrderModalActive(false);
            await handleMarkAsReceived();
          }}
          title="Close purchase order"
        />
        <Layout>
          {errorMessage ? (
            <Layout.Section>
              <ErrorBanner errorMessage={errorMessage} setErrorMessage={setErrorMessage} />
            </Layout.Section>
          ) : null}
          <Layout.Section>
            <LegacyCard>
              <ImportFile<IPurchaseOrderLineItem>
                apiCallMethod={async (formData) => await importPurchaseOrderLineItems(purchaseOrder.id, formData)}
                modalIsOpen={importCSVModalIsOpen}
                setApiResponse={(data: IPurchaseOrderLineItem[]): void => {
                  setPolis(makeLineItemsMap<SortablePurchaseOrderLineItem>(data as SortablePurchaseOrderLineItem[]));
                }}
                setErrorMessage={setErrorMessage}
                setModalIsOpen={setImportCSVModalIsOpen}
                title="Import purchase order CSV"
              />
              {purchaseOrder.supplier ? (
                <LegacyCard.Section title="Supplier">
                  <Label id="poSupplierName">{purchaseOrder.supplier.name}</Label>
                </LegacyCard.Section>
              ) : (
                <LegacyCard.Section title="Vendor">
                  <Label id="poVendor">
                    <HorseLink url={vendorPath(purchaseOrder.vendor)}>{purchaseOrder.vendor}</HorseLink>
                  </Label>
                </LegacyCard.Section>
              )}
              <LegacyCard.Section title="Destination">
                <Label id="poDestinationName">{purchaseOrder.destination}</Label>
              </LegacyCard.Section>
              <LegacyCard.Section>
                <FormLayout>
                  <FormLayout.Group>
                    <TextField
                      autoComplete="off"
                      label="Label"
                      onChange={handleLabelChange}
                      placeholder={purchaseOrder.id.toString()}
                      value={purchaseOrder.label || ""}
                    />
                    <CustomDatePicker
                      date={purchaseOrder.arrival_date}
                      label="Arrival date"
                      placeholder={today}
                      setDate={handleDateChange}
                    />
                    {/* <TextField
                        onChange={handleImportEntryChange}
                        value={importEntryNumber}
                        label="Customs form number"
                      /> */}
                  </FormLayout.Group>
                </FormLayout>
              </LegacyCard.Section>
            </LegacyCard>
          </Layout.Section>
          <Layout.Section>
            <LegacyCard>
              <LegacyCard.Section>
                <LegacyStack distribution="trailing">
                  <Scanner incrementLineItem={incrementLineItem} lineItems={polis} />
                  <Button onClick={handleReceiveAll}>Receive all</Button>
                  <Select
                    disabled={isDirty}
                    label="Sort by"
                    labelInline
                    onChange={setSortedValue}
                    options={orderedClosedPurchaseOrderSortOptions}
                    value={sortedValue}
                  />
                </LegacyStack>
              </LegacyCard.Section>
              <IndexTable
                headings={[
                  { title: "Variant" },
                  { title: "Warehouse location" },
                  // { title: 'Customs form line number' },
                  // { title: 'Description of goods' },
                  // { title: 'Classification number' },
                  // { title: 'Duty rate' },
                  { title: "Quantity ordered" },
                  { title: "Quantity received" },
                  { title: "Cost" },
                  // { title: 'Duties' },
                  { title: "Line total" },
                ]}
                itemCount={sortedLineItems.length}
                selectable={false}
              >
                <IndexTable.Row id="purchaseOrderOrderedTotalsRow" key={0} position={0} rowType="subheader">
                  <IndexTable.Cell>Totals</IndexTable.Cell>
                  {/* <IndexTable.Cell/>
                      <IndexTable.Cell/>
                      <IndexTable.Cell/>
                      <IndexTable.Cell/> */}
                  <IndexTable.Cell />
                  <IndexTable.Cell>{commaDelimiter(total.quantity)}</IndexTable.Cell>
                  <IndexTable.Cell>
                    {commaDelimiter(total.received)}
                  </IndexTable.Cell>
                  <IndexTable.Cell>{formatMoney(total.cost, purchaseOrder.currency)}</IndexTable.Cell>
                  {/* <IndexTable.Cell>
                        { formatMoney(total.duties, purchaseOrder.currency) }
                      </IndexTable.Cell> */}
                  <IndexTable.Cell>{formatMoney(total.totalCost, purchaseOrder.currency)}</IndexTable.Cell>
                </IndexTable.Row>
                {sortedLineItems.map((variant: SortablePurchaseOrderLineItem, index) => (
                  <PurchaseOrderOrderedRow
                    handleLineItemChange={handleLineItemChange}
                    index={index + 1}
                    key={`purchase-order-ordered-row:${variant.id}`}
                    purchaseOrder={purchaseOrder}
                    purchaseOrderLineItem={variant}
                  />
                ))}
              </IndexTable>
            </LegacyCard>
          </Layout.Section>
          <Layout.Section variant="oneHalf">
            <LegacyCard title="Note to supplier">
              <LegacyCard.Section>
                <TextField
                  autoComplete="off"
                  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                  error={purchaseOrder.note?.length >= 1000 ? "Note is too long" : ""}
                  label="Note"
                  maxLength={1000}
                  multiline={2}
                  onChange={handleNoteChange}
                  value={purchaseOrder.note || ""}
                />
              </LegacyCard.Section>
            </LegacyCard>
          </Layout.Section>
          <Layout.Section variant="oneHalf">
            <LegacyCard>
              <LegacyCard.Section>
                <CostAdjustments
                  currency={purchaseOrder.currency}
                  discardCallback={summaryDiscardCallback}
                  lineTotal={Number(total.totalCost)}
                  purchaseOrder={purchaseOrder}
                  receivedCost={Number(total.receivedCost)}
                  saveCallback={summarySaveCallback}
                  setErrorMessage={setErrorMessage}
                  setIsDirty={setSummaryIsDirty}
                />
              </LegacyCard.Section>
            </LegacyCard>
          </Layout.Section>
        </Layout>
        <PageActions
          secondaryActions={[
            {
              content: "Delete",
              destructive: true,
              onAction: openDeletionDialogue,
            },
          ]}
        />
        <DeleteConfirmationDialog
          active={deletionDialogueActive}
          content="Are you sure you want to delete this purchase order? This action cannot be reversed."
          deleteItem={deletePurchaseOrder}
          gotoPath="/purchase_orders"
          itemId={purchaseOrder.id}
          setActive={setDeletionDialogueActive}
          title="Delete purchase"
          toastMessage="Purchase order deleted"
        />
        <div style={{ maxWidth: "640px", margin: "0 auto" }}>
          <Comments />
        </div>
        <Footer pageTitle="purchase orders" url={PURCHASE_ORDER_HELP_PAGE_URL} />
      </Page>
    )
  );
}

export default Index;
